import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { ArrowRight, Building2, CheckCircle2, Church, Network } from "lucide-react"
import ChurchConnectCard from "./ChurchConnectCard"
import { StatusMessage } from "../ui/StatusMessage"
import { getApiBaseUrl } from "../../config/apiBase"
import "../../styles/components/ChurchOnboarding.css"

export default function ChurchInviteJoinPage() {
  const { token } = useParams()
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [selectedChurchId, setSelectedChurchId] = useState("")
  const [fellowshipResult, setFellowshipResult] = useState(null)
  const [fellowshipError, setFellowshipError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const apiUrl = getApiBaseUrl()

  useEffect(() => {
    const loadInvite = async () => {
      setLoading(true)
      setError("")
      try {
        const response = await fetch(`${apiUrl}/api/ChurchInvites/${token}`)
        if (!response.ok) {
          throw new Error("This church invite is unavailable or has expired.")
        }
        setInvite(await response.json())
      } catch (err) {
        setError(err.message || "Could not load this church invite.")
      } finally {
        setLoading(false)
      }
    }

    loadInvite()
  }, [apiUrl, token])

  const connectFellowship = async () => {
    setFellowshipError("")

    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: `${window.location.pathname}${window.location.search}` },
      })
      return
    }

    if (!selectedChurchId) {
      setFellowshipError("Enter the TYK group id for the church you represent.")
      return
    }

    setSubmitting(true)
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const response = await fetch(`${apiUrl}/api/ChurchInvites/${token}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ churchGroupId: Number(selectedChurchId) }),
      })

      if (!response.ok) {
        throw new Error("Could not connect this church to the fellowship network.")
      }

      setFellowshipResult(await response.json())
    } catch (err) {
      setFellowshipError(err.message || "Could not connect this church.")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main className="church-onboarding-page">
        <StatusMessage tone="loading" title="Loading church invite" message="Opening this church connection link." />
      </main>
    )
  }

  if (error || !invite) {
    return (
      <main className="church-onboarding-page">
        <StatusMessage tone="error" title="Invite unavailable" message={error || "This invite could not be found."} />
      </main>
    )
  }

  const isFellowship = invite.inviteType === "Fellowship"

  return (
    <main className="church-onboarding-page">
      <section className="church-invite-hero">
        <span className="church-onboarding-kicker">
          {isFellowship ? <Network size={16} aria-hidden="true" /> : <Church size={16} aria-hidden="true" />}
          {isFellowship ? "Fellowship invitation" : "Church invitation"}
        </span>
        <h1>{isFellowship ? "Connect your church" : `Connect with ${invite.groupName}`}</h1>
        <p>
          {isFellowship
            ? `${invite.groupName} is inviting peer churches to share visibility, coordination, and fellowship inside TYK.`
            : "Join the church body in TYK so you can see the dashboard, participate where invited, and help the Body of Christ move together."}
        </p>
        {invite.denominationOrTradition && <strong>{invite.denominationOrTradition}</strong>}
      </section>

      {isFellowship ? (
        <section className="church-onboarding-panel">
          {fellowshipResult ? (
            <div className="church-connect-success">
              <CheckCircle2 size={20} aria-hidden="true" />
              <div>
                <strong>Fellowship connected</strong>
                <span>{fellowshipResult.message}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="church-connect-heading">
                <Building2 size={20} aria-hidden="true" />
                <div>
                  <strong>Which church do you represent?</strong>
                  <span>Enter the TYK group id for your church. Only a church representative can connect it.</span>
                </div>
              </div>
              <label className="church-field">
                Church group id
                <input
                  type="number"
                  value={selectedChurchId}
                  onChange={(event) => setSelectedChurchId(event.target.value)}
                  placeholder="Example: 42"
                />
              </label>
              {fellowshipError && <div className="church-connect-error" role="alert">{fellowshipError}</div>}
              <button type="button" className="church-connect-action" onClick={connectFellowship} disabled={submitting}>
                <ArrowRight size={16} aria-hidden="true" />
                {submitting ? "Connecting..." : "Connect church to fellowship"}
              </button>
            </>
          )}
        </section>
      ) : (
        <section className="church-onboarding-panel">
          <ChurchConnectCard
            churchRecord={{
              groupId: invite.groupId,
              groupName: invite.groupName,
              membershipMode: invite.membershipMode,
            }}
          />
        </section>
      )}

      <Link className="church-onboarding-link" to="/churches">
        Find another church
      </Link>
    </main>
  )
}
